import { useState, useContext } from "react";
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Button, 
  Switch, 
  Select, 
  InputNumber, 
  Space, 
  Divider, 
  Row, 
  Col, 
  Tabs,
  Alert,
  message,
  Upload,
  ColorPicker,
  Slider,
  Radio,
  Checkbox,
  Tag,
  Tooltip,
  Badge,
  Progress
} from "antd";
import { 
  SettingOutlined, 
  SaveOutlined, 
  ReloadOutlined, 
  SecurityScanOutlined,
  DatabaseOutlined,
  MailOutlined,
  NotificationOutlined,
  GlobalOutlined,
  UserOutlined,
  LockOutlined,
  CloudOutlined,
  MonitorOutlined,
  FileTextOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import "./AdminSettings.css";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const AdminSettings = () => {
  const { isDark } = useContext(ThemeContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  const handleSave = async (values) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log("Settings saved:", values);
      message.success("Settings saved successfully!");
    } catch (error) {
      message.error("Failed to save settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    message.info("Settings reset to default values");
  };

  const renderGeneralSettings = () => (
    <div className="settings-section" style={{ padding: "0 24px" }}>
      <Title level={4} style={{ textAlign: "left", marginBottom: 24, display: "flex", alignItems: "center", gap: 8, color: isDark ? '#ffffff' : undefined }}>
        <GlobalOutlined style={{ color: isDark ? '#ffffff' : undefined }} /> General Settings
      </Title>
      
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <Form.Item
            name="siteName"
            label="Site Name"
            rules={[{ required: true, message: "Please enter site name" }]}
          >
            <Input placeholder="Enter site name" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="siteUrl"
            label="Site URL"
            rules={[{ required: true, message: "Please enter site URL" }]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="timezone"
            label="Default Timezone"
            rules={[{ required: true, message: "Please select timezone" }]}
          >
            <Select placeholder="Select timezone">
              <Option value="UTC">UTC</Option>
              <Option value="America/New_York">Eastern Time</Option>
              <Option value="America/Chicago">Central Time</Option>
              <Option value="America/Denver">Mountain Time</Option>
              <Option value="America/Los_Angeles">Pacific Time</Option>
              <Option value="Europe/London">London</Option>
              <Option value="Europe/Paris">Paris</Option>
              <Option value="Asia/Tokyo">Tokyo</Option>
              <Option value="Asia/Shanghai">Shanghai</Option>
              <Option value="Asia/Kolkata">Mumbai</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="language"
            label="Default Language"
            rules={[{ required: true, message: "Please select language" }]}
          >
            <Select placeholder="Select language">
              <Option value="en">English</Option>
              <Option value="es">Spanish</Option>
              <Option value="fr">French</Option>
              <Option value="de">German</Option>
              <Option value="it">Italian</Option>
              <Option value="pt">Portuguese</Option>
              <Option value="ru">Russian</Option>
              <Option value="ja">Japanese</Option>
              <Option value="ko">Korean</Option>
              <Option value="zh">Chinese</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="siteDescription"
            label="Site Description"
          >
            <TextArea 
              placeholder="Enter site description"
              rows={3}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section" style={{ padding: "0 24px" }}>
      <Title level={4} style={{ textAlign: "left", marginBottom: 24, display: "flex", alignItems: "center", gap: 8, color: isDark ? '#ffffff' : undefined }}>
        <SecurityScanOutlined style={{ color: isDark ? '#ffffff' : undefined }} /> Security Settings
      </Title>
      
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <Form.Item
            name="sessionTimeout"
            label="Session Timeout (minutes)"
            rules={[{ required: true, message: "Please enter session timeout" }]}
          >
            <InputNumber 
              min={5} 
              max={1440} 
              placeholder="30"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="maxLoginAttempts"
            label="Max Login Attempts"
            rules={[{ required: true, message: "Please enter max login attempts" }]}
          >
            <InputNumber 
              min={3} 
              max={10} 
              placeholder="5"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="passwordMinLength"
            label="Minimum Password Length"
            rules={[{ required: true, message: "Please enter minimum password length" }]}
          >
            <InputNumber 
              min={6} 
              max={20} 
              placeholder="8"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="passwordExpiry"
            label="Password Expiry (days)"
            rules={[{ required: true, message: "Please enter password expiry days" }]}
          >
            <InputNumber 
              min={0} 
              max={365} 
              placeholder="90"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="enableTwoFactor"
            label="Two-Factor Authentication"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="enableCaptcha"
            label="Enable CAPTCHA on Login"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="enableIpWhitelist"
            label="Enable IP Whitelist"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="settings-section" style={{ padding: "0 24px" }}>
      <Title level={4} style={{ textAlign: "left", marginBottom: 24, display: "flex", alignItems: "center", gap: 8, color: isDark ? '#ffffff' : undefined }}>
        <MailOutlined style={{ color: isDark ? '#ffffff' : undefined }} /> Email Settings
      </Title>
      
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <Form.Item
            name="smtpHost"
            label="SMTP Host"
            rules={[{ required: true, message: "Please enter SMTP host" }]}
          >
            <Input placeholder="smtp.gmail.com" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="smtpPort"
            label="SMTP Port"
            rules={[{ required: true, message: "Please enter SMTP port" }]}
          >
            <InputNumber 
              min={1} 
              max={65535} 
              placeholder="587"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="smtpUsername"
            label="SMTP Username"
            rules={[{ required: true, message: "Please enter SMTP username" }]}
          >
            <Input placeholder="your-email@gmail.com" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="smtpPassword"
            label="SMTP Password"
            rules={[{ required: true, message: "Please enter SMTP password" }]}
          >
            <Input.Password placeholder="Enter SMTP password" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="fromEmail"
            label="From Email Address"
            rules={[{ required: true, message: "Please enter from email" }]}
          >
            <Input placeholder="noreply@example.com" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="fromName"
            label="From Name"
            rules={[{ required: true, message: "Please enter from name" }]}
          >
            <Input placeholder="Your Company Name" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="enableEmailNotifications"
            label="Enable Email Notifications"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section" style={{ padding: "0 24px" }}>
      <Title level={4} style={{ textAlign: "left", marginBottom: 24, display: "flex", alignItems: "center", gap: 8, color: isDark ? '#ffffff' : undefined }}>
        <NotificationOutlined style={{ color: isDark ? '#ffffff' : undefined }} /> Notification Settings
      </Title>
      
      <Row gutter={[24, 16]}>
        <Col xs={24}>
          <Form.Item
            name="enablePushNotifications"
            label="Enable Push Notifications"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="enableSmsNotifications"
            label="Enable SMS Notifications"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="notificationTypes"
            label="Notification Types"
          >
            <Checkbox.Group options={[
              { label: 'User Registration', value: 'user_registration' },
              { label: 'Password Reset', value: 'password_reset' },
              { label: 'System Alerts', value: 'system_alerts' },
              { label: 'Security Events', value: 'security_events' },
              { label: 'Performance Warnings', value: 'performance_warnings' },
              { label: 'Backup Notifications', value: 'backup_notifications' }
            ]} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="notificationFrequency"
            label="Notification Frequency"
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="immediate">Immediate</Radio>
                <Radio value="hourly">Hourly Digest</Radio>
                <Radio value="daily">Daily Digest</Radio>
                <Radio value="weekly">Weekly Digest</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderDatabaseSettings = () => (
    <div className="settings-section" style={{ padding: "0 24px" }}>
      <Title level={4} style={{ textAlign: "left", marginBottom: 24, display: "flex", alignItems: "center", gap: 8, color: isDark ? '#ffffff' : undefined }}>
        <DatabaseOutlined style={{ color: isDark ? '#ffffff' : undefined }} /> Database Settings
      </Title>
      
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <Form.Item
            name="dbHost"
            label="Database Host"
            rules={[{ required: true, message: "Please enter database host" }]}
          >
            <Input placeholder="localhost" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="dbPort"
            label="Database Port"
            rules={[{ required: true, message: "Please enter database port" }]}
          >
            <InputNumber 
              min={1} 
              max={65535} 
              placeholder="5432"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="dbName"
            label="Database Name"
            rules={[{ required: true, message: "Please enter database name" }]}
          >
            <Input placeholder="mydatabase" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="dbUsername"
            label="Database Username"
            rules={[{ required: true, message: "Please enter database username" }]}
          >
            <Input placeholder="dbuser" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="enableBackup"
            label="Enable Automatic Backups"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="backupFrequency"
            label="Backup Frequency"
          >
            <Select placeholder="Select backup frequency">
              <Option value="daily">Daily</Option>
              <Option value="weekly">Weekly</Option>
              <Option value="monthly">Monthly</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="backupRetention"
            label="Backup Retention (days)"
          >
            <InputNumber 
              min={1} 
              max={365} 
              placeholder="30"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderPerformanceSettings = () => (
    <div className="settings-section" style={{ padding: "0 24px" }}>
      <Title level={4} style={{ textAlign: "left", marginBottom: 24, display: "flex", alignItems: "center", gap: 8, color: isDark ? '#ffffff' : undefined }}>
        <MonitorOutlined style={{ color: isDark ? '#ffffff' : undefined }} /> Performance Settings
      </Title>
      
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <Form.Item
            name="cacheEnabled"
            label="Enable Caching"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="cacheDuration"
            label="Cache Duration (minutes)"
          >
            <InputNumber 
              min={1} 
              max={1440} 
              placeholder="60"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="maxConcurrentUsers"
            label="Max Concurrent Users"
          >
            <InputNumber 
              min={10} 
              max={10000} 
              placeholder="1000"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="requestTimeout"
            label="Request Timeout (seconds)"
          >
            <InputNumber 
              min={5} 
              max={300} 
              placeholder="30"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="performanceLevel"
            label="Performance Level"
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="low">Low (Basic)</Radio>
                <Radio value="medium">Medium (Standard)</Radio>
                <Radio value="high">High (Premium)</Radio>
                <Radio value="ultra">Ultra (Enterprise)</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  return (
    <div className={`admin-settings-container ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div
        className="admin-settings-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          {isDark && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)',
                borderRadius: 16,
              }}
            />
          )}
          
          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Title
                level={1}
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                  textShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                }}
              >
                System Settings
              </Title>
              <Text 
                style={{ 
                  fontSize: 20, 
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
                }}
              >
                Configure system preferences, security, and application settings
              </Text>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button 
                icon={<SaveOutlined />}
                onClick={() => form.submit()}
                loading={loading}
                style={{
                  borderRadius: 8,
                  height: 40,
                  fontWeight: 500,
                  backgroundColor: isDark ? '#3a3a4e' : 'white',
                  borderColor: isDark ? '#4a4a5e' : '#d9d9d9',
                  color: isDark ? '#ffffff' : '#000'
                }}
              >
                Save Settings
              </Button>
              <Button 
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleReset}
                style={{
                  borderRadius: 8,
                  height: 40,
                  fontWeight: 500,
                  backgroundColor: isDark ? '#8b5cf6' : undefined,
                  borderColor: isDark ? '#8b5cf6' : undefined
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, backgroundColor: isDark ? '#2a2a3e' : '#f5f5f5', borderRadius: 12, padding: 4, marginBottom: 24 }}>
            <div
              onClick={() => setActiveTab("general")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "general" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <GlobalOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>General</span>
            </div>
            
            <div
              onClick={() => setActiveTab("security")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "security" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <SecurityScanOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Security</span>
            </div>
            
            <div
              onClick={() => setActiveTab("email")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "email" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <MailOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Email</span>
            </div>
            
            <div
              onClick={() => setActiveTab("notifications")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "notifications" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <NotificationOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Notifications</span>
            </div>
            
            <div
              onClick={() => setActiveTab("database")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "database" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <DatabaseOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Database</span>
            </div>
            
            <div
              onClick={() => setActiveTab("performance")}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: activeTab === "performance" ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                transition: 'all 0.2s ease',
                width: 150
              }}
            >
              <MonitorOutlined style={{ fontSize: 20, color: isDark ? '#ffffff' : '#333', marginBottom: 4, fontWeight: 'bold' }} />
              <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333' }}>Performance</span>
            </div>
          </div>

      {/* Settings Content */}
      <div className="settings-content">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          className="settings-form"
          initialValues={{
            siteName: "SimplyBond Admin",
            siteUrl: "https://admin.simplybond.com",
            timezone: "UTC",
            language: "en",
            sessionTimeout: 30,
            maxLoginAttempts: 5,
            passwordMinLength: 8,
            passwordExpiry: 90,
            enableTwoFactor: true,
            enableCaptcha: false,
            enableEmailNotifications: true,
            enablePushNotifications: true,
            cacheEnabled: true,
            cacheDuration: 60,
            maxConcurrentUsers: 1000,
            requestTimeout: 30,
            performanceLevel: "medium"
          }}
        >
          {/* Tab Content */}
          <div>
            {activeTab === "general" && renderGeneralSettings()}
            {activeTab === "security" && renderSecuritySettings()}
            {activeTab === "email" && renderEmailSettings()}
            {activeTab === "notifications" && renderNotificationSettings()}
            {activeTab === "database" && renderDatabaseSettings()}
            {activeTab === "performance" && renderPerformanceSettings()}
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AdminSettings; 